/**
 * Profile list viewed event – tracks when users view lists of providers/facilities
 * Used for referral results, compare views, affiliated provider lists, etc.
 */

import { z } from 'zod';

import { defineEvent } from '../event-factory';
import { buildTrackingKey, flattenForTrackingKey } from '../utils';

// Define reusable schemas for complex objects
const ListParameterSchema = z.object({
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  speciatlyKey: z.string().optional(), // Note: keeping typo to match official schema
  procedureKey: z.string().optional(),
  conditionKey: z.string().optional(),
  officeKey: z.string().optional(),
  clientKey: z.string().optional(),
  searchName: z.string().optional(),
  facilityKey: z.string().optional(),
});

const KeyValueSchema = z.object({
  key: z.string(),
  value: z.string(),
});

const SponsorContextSchema = z.object({
  clientKey: z.string().optional(),
  productKey: z.string().optional(),
  facilityKey: z.string().optional(),
  geographicAreaKey: z.string().optional(),
  configProperties: z.array(KeyValueSchema).optional(),
});

const ListItemSchema = z.object({
  hgProfileKey: z.string(),
  position: z.number(),
  clientKey: z.string().optional(), // For facilities
  badgeProperties: z.array(z.string()).optional(),
  schedulingProperties: z.array(z.string()).optional(),
  customProperties: z.array(KeyValueSchema).optional(),
});

export default defineEvent({
  eventName: 'healthgrades.ProfileListViewed.v1',

  // What data this event needs (see schema link for full details)
  // https://app.make.rvohealth.com/schemas/sch_2wMqOs3y6SbrPNaitOtQEJ4qSKP
  cohesionSchema: {
    entityType: z.enum(['Provider', 'Facility']),
    listType: z.string(), // "Referral Results", "Referral Compare", etc.
    viewCorrelationID: z.string().optional(),
    listParameters: ListParameterSchema.optional(),
    filterParameters: z.array(KeyValueSchema).optional(),
    sortOrder: z.string().optional(), // "Best Match", "Distance", etc.
    listItems: z.array(ListItemSchema).optional(),
    sponsorContext: SponsorContextSchema.optional(),
    programID: z.string().optional(),
  },

  tealiumMethodType: 'link',

  // Deduplication: One event per unique list configuration per page view
  deduplication: {
    scope: 'page',
    keyBuilder: (params) =>
      buildTrackingKey({
        entityType: params.entityType,
        listType: params.listType,
        ...(params.listParameters
          ? flattenForTrackingKey(params.listParameters)
          : {}),
        ...(params.filterParameters
          ? flattenForTrackingKey(params.filterParameters)
          : {}),
        sortOrder: params.sortOrder,
      }),
  },
});
